/**
 * Consola de oficina — el armazón.
 *
 * 🚨 Reescrita el 21-ago-2026 después de que Luis rechazara la anterior tres
 * veces seguidas. Su reclamo de fondo, y el que lo cambia todo:
 *
 *   «Para remate, esto lo van a usar en computador y lo estás haciendo en
 *    pantalla de celular. Después tienes que hacerlo en pantalla de celular,
 *    pero su uso principalmente está pensado para PC.»
 *
 * Yo le había armado la consola como una **columna larga apilada**, que es la
 * forma de un celular. En un PC de 1600×900 medí: Entregas ocupaba 2.394 px de
 * alto —1.492 px de scroll, casi dos pantallas— y no había una sola gráfica.
 *
 * ⭐ Estructura aprobada por él en maqueta antes de escribir esto:
 *
 *   barra superior navy (56 px)
 *   ─── franja del degradado de marca (3 px) ───
 *   ┌── LATERAL 232 px ──┬── ÁREA DE TRABAJO ──┐
 *   │ agrupada, con      │ scrollea SOLA: la   │
 *   │ contadores por     │ cabecera y el menú  │
 *   │ opción             │ no se van nunca     │
 *
 * El contador al lado de cada opción es lo que hace que la barra sirva: se ve
 * que hay 7 manifiestos sin subir **sin tener que entrar a mirarlos**.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { PanelSalud } from './PanelSalud.tsx'
import { Conductores } from './Conductores.tsx'
import { Entregas } from './Entregas.tsx'
import { Manifiestos } from './Manifiestos.tsx'
import { Personas } from './Personas.tsx'
import { Tablero, type DatosTablero } from './Tablero.tsx'
import { Clientes } from './Clientes.tsx'
import { Clasificar } from './Clasificar.tsx'
import { Turnos } from './Turnos.tsx'
import { Entrar, tokenGuardado, salirDeLaConsola, pedirConSesion, quienEntro } from './Entrar.tsx'
import type { SaludSync, DatosBandejas } from './tipos.ts'

type Pestana =
  | 'tablero' | 'entregas' | 'manifiestos' | 'salud'
  | 'bandejas' | 'conductores' | 'personas' | 'clientes' | 'turnos'

/** Los dibujos del menú. Lucide, nunca emoji (regla 25). */
const ICONOS: Record<string, ReactNode> = {
  tablero: (
    <>
      <rect x="3" y="3" width="7" height="9" /><rect x="14" y="3" width="7" height="5" />
      <rect x="14" y="12" width="7" height="9" /><rect x="3" y="16" width="7" height="5" />
    </>
  ),
  camion: (
    <>
      <path d="M5 18H3V6h11v12H9" /><path d="M14 9h4l3 3v6h-3" />
      <circle cx="7" cy="18" r="2" /><circle cx="17" cy="18" r="2" />
    </>
  ),
  entregas: (
    <>
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </>
  ),
  papel: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" /><path d="M8 13h8M8 17h5" />
    </>
  ),
  clientes: (
    <>
      <path d="M3 21h18M5 21V7l7-4 7 4v14" /><path d="M9 21v-6h6v6" />
    </>
  ),
  persona: (
    <>
      <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
    </>
  ),
  llave: (
    <>
      <rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </>
  ),
  pulso: <path d="M22 12h-4l-3 9L9 3l-3 9H2" />,
  clasificar: (
    <>
      <path d="M3 6h18M6 12h12M10 18h4" />
    </>
  ),
}

function Icono({ nombre }: { nombre: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
      {ICONOS[nombre]}
    </svg>
  )
}

/** Una opción del menú, con su contador. */
function Opcion({
  id, texto, icono, cuantos, tono = 'gris', activa, alTocar,
}: {
  id: Pestana
  texto: string
  icono: string
  cuantos?: number | undefined
  tono?: 'gris' | 'ambar' | 'rojo' | 'verde'
  activa: Pestana
  alTocar: (p: Pestana) => void
}) {
  return (
    <button
      onClick={() => alTocar(id)}
      className={`op ${activa === id ? 'op-activa' : ''}`}
      aria-current={activa === id ? 'page' : undefined}
    >
      <Icono nombre={icono} />
      {texto}
      {/* Un cero no se pinta: un contador que siempre dice 0 es ruido. */}
      {cuantos != null && cuantos > 0 && <b className={`pill-${tono} num`}>{cuantos}</b>}
    </button>
  )
}

export function App() {
  const [entrado, setEntrado] = useState(() => tokenGuardado() !== null)
  const [pestana, setPestana] = useState<Pestana>('tablero')
  const [salud, setSalud] = useState<SaludSync | null>(null)
  const [bandejas, setBandejas] = useState<DatosBandejas | null>(null)
  const [tablero, setTablero] = useState<DatosTablero | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [cargando, setCargando] = useState(true)

  const yo = quienEntro()

  /*
   * 🚨 CUÁNDO FUE EL ÚLTIMO BARRIDO, visible donde la gente está.
   *
   * Salió de la auditoría: si el barrido se cae, la consola sigue mostrando los
   * mismos números **sin avisar**, y alguien puede pasarse la mañana trabajando
   * sobre datos de ayer. Tenerlo solo dentro de «Salud del sync» no sirve:
   * nadie abre esa pantalla hasta que ya sospecha.
   *
   * 📊 Medido: corre **cada 10 minutos** —144 corridas en 24 h, exactas—. Por
   * eso 30 minutos ya es raro y una hora es que algo se rompió.
   */
  const minutosSinBarrer =
    salud && salud.segundosDesdeUltima >= 0 ? Math.round(salud.segundosDesdeUltima / 60) : null
  const barridoAtrasado = minutosSinBarrer !== null && minutosSinBarrer >= 30
  const barridoCaido = minutosSinBarrer !== null && minutosSinBarrer >= 60

  /*
   * Una sola carga para los tres. El tablero además alimenta los contadores del
   * menú, así que pedirlo aquí evita que la misma consulta se haga dos veces.
   */
  const cargar = useCallback(async () => {
    try {
      const [s, b, t] = await Promise.all([
        pedirConSesion<SaludSync>('/api/salud-sync'),
        pedirConSesion<DatosBandejas>('/api/bandejas'),
        pedirConSesion<DatosTablero>('/api/admin/tablero'),
      ])
      setSalud(s)
      setBandejas(b)
      setTablero(t)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => {
    if (!entrado) return
    void cargar()
    // La operación se mueve todo el día; un minuto basta para que nadie esté
    // mirando algo de hace media hora.
    const t = setInterval(() => void cargar(), 60_000)
    return () => clearInterval(t)
  }, [entrado, cargar])

  /*
   * 🚨 Los TOTALES, no el largo de las listas.
   *
   * Cada lista viene recortada a 200, así que sumar sus largos daba 464 cuando
   * de verdad hay 566 camiones y 581 conductores: el contador del menú —lo
   * primero que se ve— decía que la pila era la mitad de lo que es.
   *
   * ⛔ Solo cuenta lo que ESA pantalla resuelve. Los clientes sin portal se
   * activan en su propia pantalla, así que no suman aquí.
   */
  const porClasificar = bandejas?.totales
    ? bandejas.totales.camiones + bandejas.totales.conductores
    : 0

  if (!entrado) return <Entrar alEntrar={() => setEntrado(true)} />

  const iniciales = (yo?.nombre ?? 'Oficina')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('')

  return (
    <div className="marco">
      <header className="arriba">
        <img src="/icon-192.png" alt="Grupo S&amp;M" />
        <div className="marca-txt">
          <b>SymTrack</b>
          <span>Operaciones integrales</span>
        </div>

        <div className="arriba-der">
          <button onClick={() => void cargar()} className="btn-arriba">
            Actualizar
          </button>
          <div className="yo">
            <div className="yo-bola">{iniciales}</div>
            <div className="yo-txt">
              <b>{yo?.nombre ?? 'Oficina'}</b>
              <span>
                {yo?.rol === 'ADMIN'
                  ? 'Administrador'
                  : yo?.rol === 'CONSULTA'
                    ? 'Solo consulta'
                    : yo
                      ? 'Logística'
                      : 'Clave compartida'}
              </span>
            </div>
          </div>
          <button onClick={salirDeLaConsola} className="btn-salir">
            Salir
          </button>
        </div>
      </header>
      {/* La franja del degradado de marca. Uno de los 4 lugares donde se usa. */}
      <div className="franja" />

      {/*
       * 🚨 Que lo sepa desde el primer segundo, no cuando le rebote un botón.
       * Antes su cuenta decía «solo consulta» y aun así podía aprobar entregas:
       * ahora el servidor se lo impide y la pantalla se lo avisa.
       */}
      {/*
       * ⛔ Solo cuando pasa algo. Una franja permanente se vuelve parte del
       * decorado y deja de leerse.
       */}
      {barridoAtrasado && (
        <div className={`tira-sync ${barridoCaido ? 'tira-sync-mal' : ''}`}>
          {barridoCaido ? (
            <>
              <b>Los datos no se están actualizando.</b> El último barrido fue hace{' '}
              {minutosSinBarrer} minutos y debería correr cada 10. Lo que ves puede estar viejo.
            </>
          ) : (
            <>
              El último barrido fue hace <b>{minutosSinBarrer} minutos</b> y normalmente corre cada
              10. Puede que algunos viajes nuevos todavía no aparezcan.
            </>
          )}
          <button onClick={() => setPestana('salud')} className="tira-sync-ir">
            Ver qué pasa
          </button>
        </div>
      )}

      {yo?.rol === 'CONSULTA' && (
        <div className="tira-consulta">
          Tu cuenta es de <b>solo consulta</b>: puedes mirar y descargar todo, pero no aprobar
          entregas ni subir manifiestos.
        </div>
      )}

      <div className="cuerpo">
        <nav className="lateral" aria-label="Secciones">
          <div className="grupo">
            <p className="grupo-tit">Operación</p>
            <Opcion id="tablero" texto="Tablero" icono="tablero" activa={pestana} alTocar={setPestana} />
            <Opcion
              id="entregas" texto="Entregas" icono="entregas"
              cuantos={tablero?.alertas.sinRevisar} tono="ambar"
              activa={pestana} alTocar={setPestana}
            />
            <Opcion
              id="manifiestos" texto="Manifiestos" icono="papel"
              cuantos={tablero?.alertas.rodandoSinManifiesto} tono="rojo"
              activa={pestana} alTocar={setPestana}
            />
            {/* Los turnos van en Operación, entre lo del día: un despachador
                pregunta «¿quién está libre?» tan seguido como «¿qué llegó?». */}
            {/* 🚨 En VERDE, no en ámbar: los otros contadores son cosas por
                resolver; este es lo contrario —camiones listos para cargar—. Un
                número que crece aquí es una buena noticia, no una alarma. */}
            <Opcion
              id="turnos" texto="Turnos" icono="camion"
              cuantos={tablero?.alertas.camionesLibres} tono="verde"
              activa={pestana} alTocar={setPestana}
            />
            <Opcion
              id="bandejas" texto="Por clasificar" icono="clasificar"
              cuantos={porClasificar}
              activa={pestana} alTocar={setPestana}
            />
          </div>

          <div className="grupo">
            <p className="grupo-tit">Administración</p>
            <Opcion id="clientes" texto="Clientes y portal" icono="clientes" activa={pestana} alTocar={setPestana} />
            <Opcion id="conductores" texto="Conductores" icono="persona" activa={pestana} alTocar={setPestana} />
            <Opcion id="personas" texto="Autorizar personas" icono="llave" activa={pestana} alTocar={setPestana} />
          </div>

          <div className="grupo">
            <p className="grupo-tit">Sistema</p>
            <button
              onClick={() => setPestana('salud')}
              className={`op ${pestana === 'salud' ? 'op-activa' : ''}`}
              aria-current={pestana === 'salud' ? 'page' : undefined}
            >
              <Icono nombre="pulso" />
              Salud del sync
              {/*
                El punto siempre está: verde es «esto se está mirando solo».
                Sin él, nadie sabe si el silencio es que va bien o que nadie
                revisó.
              */}
              <span
                className={`pulso-punto ${barridoCaido ? 'pulso-mal' : barridoAtrasado ? 'pulso-aviso' : 'pulso-bien'}`}
                title={
                  minutosSinBarrer === null
                    ? 'Sin datos del barrido'
                    : `Último barrido hace ${minutosSinBarrer} min`
                }
              />
            </button>
          </div>

          <div className="pie-lat">
            Grupo S&amp;M · Operaciones Integrales
            <br />
            NIT 900176548-4
          </div>
        </nav>

        <main className="trabajo">
          {error && (
            <div className="aviso-error">
              <span className="punto" style={{ background: '#CA2B2B' }} />
              <div>
                <b>No se pudieron cargar los datos</b>
                <p className="num">{error}</p>
              </div>
            </div>
          )}

          {cargando && !salud && pestana !== 'conductores' && pestana !== 'entregas' && (
            <p className="cargando">Cargando…</p>
          )}

          {pestana === 'tablero' && <Tablero datos={tablero} alIrA={(p) => setPestana(p as Pestana)} />}
          {pestana === 'entregas' && <Entregas />}
          {pestana === 'manifiestos' && <Manifiestos />}
          {pestana === 'turnos' && <Turnos />}
          {pestana === 'bandejas' && <Clasificar alIrA={(p) => setPestana(p as Pestana)} totales={bandejas?.totales} />}
          {pestana === 'clientes' && <Clientes />}
          {pestana === 'conductores' && <Conductores />}
          {pestana === 'personas' && <Personas />}
          {pestana === 'salud' && salud && <PanelSalud datos={salud} />}
        </main>
      </div>
    </div>
  )
}
